/*Import Packages*/
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

/*Import global components like header, footers (always needed)*/
import Sidebar from './components/navigation/sidebar.js';
import Topbar from './components/navigation/topbar.js';
import Footer from './components/navigation/footer.js';
import Loader from './components/common/loader.js';

/*Import auth pages (needed immediately)*/
import LoginMain from './containers/auth/main.js';
import ForgotPassword from './containers/auth/forgotPassword.js';
import SetAdminPassword from './containers/auth/set-password.js';

/*Lazy load pages for code splitting*/
const DashboardDynamic = lazy(() => import('./containers/dashboard/dynamic.js'));
const ManageCards = lazy(() => import('./containers/dashboard/manageCards.js'));
const ActiveUsers = lazy(() => import('./containers/appUsers/activeUsers.js'));
const BlockedUsers = lazy(() => import('./containers/appUsers/blockedUsers.js'));
const PendingApprovals = lazy(() => import('./containers/appUsers/pendingApprovals.js'));
const DeactivatedUsers = lazy(() => import('./containers/appUsers/deactivatedUsers.js'));
const ExpiringDocuments = lazy(() => import('./containers/appUsers/expiringDocuments.js'));
const DocumentRequests = lazy(() => import('./containers/appUsers/documentRequests.js'));
const UserDetail = lazy(() => import('./containers/appUsers/detail.js'));
const BeneficiariesList = lazy(() => import('./containers/beneficiaries/main.js'));
const BlockedBeneficiariesList = lazy(() => import('./containers/beneficiaries/blocked-beneficiaries.js'));
const DomesticBeneficiariesList = lazy(() => import('./containers/beneficiaries/domestic-beneficiaries.js'));
const TransactionsList = lazy(() => import('./containers/transactions/main.js'));
const PGTransactionsList = lazy(() => import('./containers/PGTransactions/list.js'));
const FailedPGReview = lazy(() => import('./containers/failedPGReview/list.js'));
const MiniStatementsList = lazy(() => import('./containers/transactions/miniStatements.js'));
const ScheduledTransactionsList = lazy(() => import('./containers/scheduledTransactions/main.js'));
const MonitoredTransaction = lazy(() => import('./containers/monitoredTransactions/list.js'));
const BankDhofarList = lazy(() => import('./containers/banks/bankDhofar/banklist.js'));
const CommissionLimit = lazy(() => import('./containers/appSettings/commissionLimit.js'));
const LegalTerms = lazy(() => import('./containers/appSettings/legalTerms.js'));
const SendNotification = lazy(() => import('./containers/appSettings/sendNotification.js'));
const ChangePassword = lazy(() => import('./containers/appSettings/changePassword.js'));
const ExchangeRate = lazy(() => import('./containers/adminSettings/exchangeRate.js'));
const TransactionCommissionOverview = lazy(() => import('./containers/adminSettings/transactionCommissionOverview.js'));
const AdminUsers = lazy(() => import('./containers/adminSettings/adminUsers.js'));
const AdminDetailPage = lazy(() => import('./containers/adminSettings/adminDetail.js'));
const MyPasskeys = lazy(() => import('./containers/adminSettings/myPasskeys.js'));
const AML = lazy(() => import('./containers/aml/list.js'));
const Tickets = lazy(() => import('./containers/tickets/list.js'));
const CashChannelingConfig = lazy(() => import('./containers/cashChanneling/list.js'));
const FaceToFaceVerification = lazy(() => import('./containers/verification/faceToFaceVerification.js'));
const AgentManagement = lazy(() => import('./containers/agents/agentManagement.js'));
const CustomerReport = lazy(() => import('./containers/reports/customerReport.js'));
const TrannaSummaryReport = lazy(() => import('./containers/reports/trannaSummaryReport.js'));
const DealerLookup = lazy(() => import('./containers/reports/dealerLookup.js'));
const UserLookup = lazy(() => import('./containers/reports/userLookup.js'));
const LookupHub = lazy(() => import('./containers/lookupHub/main.js'));
const StuckTransactionsMonitor = lazy(() => import('./containers/stuckTransactions/main.js'));
const GodAdmin = lazy(() => import('./containers/godAdmin/main.js'));
const UploadTest = lazy(() => import('./containers/uploadTest/main.js'));

const App = () => {
  const isAuthenticated = sessionStorage.getItem("accessToken");

  if (!isAuthenticated) {
    // Save intended URL so we can redirect after login
    const currentPath = window.location.pathname + window.location.search;
    if (currentPath && currentPath !== '/' && currentPath !== '/forgot-password' && !currentPath.startsWith('/set-password')) {
      localStorage.setItem("redirectAfterLogin", currentPath);
    }
  }

  if (isAuthenticated) {
    return (
      <BrowserRouter>
        <div className="e-body">
          <Topbar />
          <div className="container-fluid">
            <div className="row">
              <Sidebar />
              <Suspense fallback={<Loader />}>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardDynamic />} />
                  <Route path="/dashboard-manage-cards" element={<ManageCards />} />
                  <Route path="/aml" element={<AML />} />
                  <Route path="/active-users" element={<ActiveUsers />} />
                  <Route path="/blocked-users" element={<BlockedUsers />} />
                  <Route path="/pending-approvals" element={<PendingApprovals />} />
                  <Route path="/deactivated-users" element={<DeactivatedUsers />} />
                  <Route path="/expiring-documents" element={<ExpiringDocuments />} />
                  <Route path="/document-requests" element={<DocumentRequests />} />
                  <Route path="/beneficiaries" element={<BeneficiariesList />} />
                  <Route path="/blocked-beneficiaries" element={<BlockedBeneficiariesList />} />
                  <Route path="/domestic-beneficiaries" element={<DomesticBeneficiariesList />} />
                  <Route path="/cash-channeling" element={<CashChannelingConfig />} />
                  <Route path="/transactions" element={<TransactionsList />} />
                  <Route path="/pg-transactions" element={<PGTransactionsList />} />
                  <Route path="/failed-pg-review" element={<FailedPGReview />} />
                  <Route path="/mini-statements" element={<MiniStatementsList key="wallet-statement" />} />
                  <Route path="/inbound-transactions" element={<MiniStatementsList key="inbound-transactions" inboundOnly />} />
                  <Route path="/scheduled-transactions" element={<ScheduledTransactionsList />} />
                  <Route path="/bank-dhofar" element={<BankDhofarList />} />
                  <Route path="/commission-reset" element={<CommissionLimit />} />
                  <Route path="/legal-terms" element={<LegalTerms />} />
                  <Route path="/send-notification" element={<SendNotification />} />
                  <Route path="/change-password" element={<ChangePassword />} />
                  <Route path="/exchange-rate" element={<ExchangeRate />} />
                  <Route path="/tickets" element={<Tickets />} />
                  <Route path="/face-to-face-verification" element={<FaceToFaceVerification />} />
                  <Route path="/agent-management" element={<AgentManagement />} />
                  <Route path="/admin-users" element={<AdminUsers />} />
                  <Route path="/admin-detail" element={<AdminDetailPage />} />
                  <Route path="/my-passkeys" element={<MyPasskeys />} />
                  <Route path="/transaction-commission-overview" element={<TransactionCommissionOverview />} />
                  <Route path="/monitored-transactions" element={<MonitoredTransaction />} />
                  <Route path="/user-detail" element={<UserDetail />} />
                  <Route path="/customer-report" element={<CustomerReport />} />
                  <Route path="/tranna-summary-report" element={<TrannaSummaryReport />} />
                  <Route path="/dealer-lookup" element={<DealerLookup />} />
                  <Route path="/user-lookup" element={<UserLookup />} />
                  <Route path="/lookup-hub" element={<LookupHub />} />
                  <Route path="/stuck-transactions" element={<StuckTransactionsMonitor />} />
                  <Route path="/tolkien" element={<GodAdmin />} />
                  <Route path="/upload-test" element={<UploadTest />} />
                </Routes>
              </Suspense>
            </div>
          </div>
        </div>
      </BrowserRouter>
    );
  }
  
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/set-password" element={<SetAdminPassword />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/" element={<LoginMain />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
};

export default App;
